/**
 * ============================================================================
 * ETCHED REFLECTION - Main Application JavaScript
 * ============================================================================
 *
 * Core functionality for the theme
 * No jQuery - vanilla JS only for performance
 *
 * FEATURES:
 * - Header scroll behavior (shrink on scroll)
 * - Lazy loading images
 * - Smooth scroll for anchor links
 * - Debounced scroll events
 *
 * ============================================================================
 */

(function() {
  'use strict';

  /**
   * Utility: Debounce function for performance
   */
  function debounce(func, wait = 100) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  /**
   * Header Scroll Behavior
   * Shrinks header from 80px to 50px on scroll
   * Syncs progress bar position with header state
   */
  const HeaderScroll = {
    header: null,
    scrollThreshold: 50,
    isScrolled: false,
    announcementBarHeight: 40, // Height of announcement bar

    init() {
      this.header = document.querySelector('.site-header');
      if (!this.header) return;

      // Check initial scroll position
      this.checkScroll();

      // Listen for scroll with debounce for performance
      window.addEventListener('scroll', debounce(() => this.checkScroll(), 10), { passive: true });
    },

    checkScroll() {
      const shouldBeScrolled = window.scrollY > this.scrollThreshold;

      if (shouldBeScrolled !== this.isScrolled) {
        this.isScrolled = shouldBeScrolled;
        this.header.classList.toggle('is-scrolled', shouldBeScrolled);

        // Update mobile nav position if open
        const mobileNav = document.querySelector('.mobile-nav');
        if (mobileNav) {
          mobileNav.classList.toggle('header-scrolled', shouldBeScrolled);
        }

        // Update progress bar position to sync with header scroll state
        this.updateProgressBarPosition();
      }
    },

    updateProgressBarPosition() {
      // Progress bar position is handled by CSS, but we ensure the class is applied
      // The CSS will handle the positioning based on .is-scrolled class
      const progressBars = document.querySelectorAll('.form-progress');
      progressBars.forEach(progressBar => {
        // Add class to body or progress bar container to help with CSS targeting
        if (this.isScrolled) {
          progressBar.classList.add('header-scrolled');
        } else {
          progressBar.classList.remove('header-scrolled');
        }
      });
    }
  };

  /**
   * Lazy Loading Images
   * Uses Intersection Observer for better performance
   */
  const LazyLoad = {
    init() {
      if (!('IntersectionObserver' in window)) {
        // Fallback: load all images immediately
        this.loadAllImages();
        return;
      }

      const options = {
        root: null,
        rootMargin: '50px 0px',
        threshold: 0.01
      };

      const observer = new IntersectionObserver((entries, observer) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            this.loadImage(entry.target);
            observer.unobserve(entry.target);
          }
        });
      }, options);

      document.querySelectorAll('img[data-src], img[loading="lazy"]').forEach(img => {
        observer.observe(img);
      });
    },

    loadImage(img) {
      if (img.dataset.src) {
        img.src = img.dataset.src;
        img.removeAttribute('data-src');
      }
      if (img.dataset.srcset) {
        img.srcset = img.dataset.srcset;
        img.removeAttribute('data-srcset');
      }
      img.classList.add('is-loaded');
    },

    loadAllImages() {
      document.querySelectorAll('img[data-src]').forEach(img => {
        this.loadImage(img);
      });
    }
  };

  /**
   * Smooth Scroll for Anchor Links
   */
  const SmoothScroll = {
    init() {
      document.querySelectorAll('a[href^="#"]').forEach(anchor => {
        anchor.addEventListener('click', (e) => {
          const targetId = anchor.getAttribute('href');
          if (targetId === '#') return;

          const targetElement = document.querySelector(targetId);
          if (!targetElement) return;

          e.preventDefault();

          const headerHeight = document.querySelector('.site-header')?.offsetHeight || 0;
          const targetPosition = targetElement.getBoundingClientRect().top + window.scrollY - headerHeight;

          window.scrollTo({
            top: targetPosition,
            behavior: 'smooth'
          });
        });
      });
    }
  };

  /**
   * Accessibility: Focus management
   */
  const AccessibilityUtils = {
    init() {
      // Add keyboard navigation indicator
      document.body.addEventListener('keydown', (e) => {
        if (e.key === 'Tab') {
          document.body.classList.add('user-is-tabbing');
        }
      });

      document.body.addEventListener('mousedown', () => {
        document.body.classList.remove('user-is-tabbing');
      });
    }
  };

  /**
   * Reduced Motion Detection
   */
  const MotionPreference = {
    prefersReducedMotion: false,

    init() {
      const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.prefersReducedMotion = mediaQuery.matches;

      mediaQuery.addEventListener('change', (e) => {
        this.prefersReducedMotion = e.matches;
        document.body.classList.toggle('reduce-motion', e.matches);
      });

      if (this.prefersReducedMotion) {
        document.body.classList.add('reduce-motion');
      }
    }
  };

  /**
   * Search Overlay
   * Opens/closes search modal when search icon is clicked
   */
  const SearchOverlay = {
    overlay: null,
    toggle: null,
    input: null,
    closeBtn: null,

    init() {
      this.toggle = document.querySelector('[data-search-toggle]');
      if (!this.toggle) return;

      // Create search overlay if it doesn't exist
      this.createOverlay();
      
      // Bind events
      this.toggle.addEventListener('click', () => this.open());
      
      if (this.closeBtn) {
        this.closeBtn.addEventListener('click', () => this.close());
      }

      // Close on overlay background click
      if (this.overlay) {
        this.overlay.addEventListener('click', (e) => {
          if (e.target === this.overlay) {
            this.close();
          }
        });
      }

      // Close on Escape key
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.overlay?.classList.contains('is-open')) {
          this.close();
        }
      });
    },

    createOverlay() {
      // Check if overlay already exists
      this.overlay = document.getElementById('search-overlay');
      if (this.overlay) {
        this.input = this.overlay.querySelector('.search-overlay__input');
        this.closeBtn = this.overlay.querySelector('.search-overlay__close');
        return;
      }

      // Create overlay HTML
      const overlayHTML = `
        <div id="search-overlay" class="search-overlay" aria-hidden="true" role="dialog" aria-label="Search">
          <div class="search-overlay__content">
            <button type="button" class="search-overlay__close" aria-label="Close search">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
            <form action="/search" method="get" class="search-overlay__form" role="search">
              <input
                type="search"
                name="q"
                class="search-overlay__input"
                placeholder="Search for products..."
                autocomplete="off"
                aria-label="Search"
              >
              <button type="submit" class="search-overlay__submit" aria-label="Submit search">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <circle cx="11" cy="11" r="8"/>
                  <path d="M21 21l-4.35-4.35"/>
                </svg>
              </button>
            </form>
            <div class="search-overlay__hint">
              Press <kbd>ESC</kbd> to close
            </div>
          </div>
        </div>
      `;

      document.body.insertAdjacentHTML('beforeend', overlayHTML);
      this.overlay = document.getElementById('search-overlay');
      this.input = this.overlay.querySelector('.search-overlay__input');
      this.closeBtn = this.overlay.querySelector('.search-overlay__close');
    },

    open() {
      if (!this.overlay) return;
      
      this.overlay.classList.add('is-open');
      this.overlay.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      
      // Focus input after animation
      setTimeout(() => {
        this.input?.focus();
      }, 100);
    },

    close() {
      if (!this.overlay) return;
      
      this.overlay.classList.remove('is-open');
      this.overlay.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      
      // Clear input
      if (this.input) {
        this.input.value = '';
      }
    }
  };

  /**
   * Initialize all modules on DOMContentLoaded
   */
  document.addEventListener('DOMContentLoaded', () => {
    HeaderScroll.init();
    LazyLoad.init();
    SmoothScroll.init();
    AccessibilityUtils.init();
    MotionPreference.init();
    SearchOverlay.init();

    // Log initialization in development
    if (window.location.hostname === 'localhost' || window.Shopify?.designMode) {
      console.log('Etched Reflection theme initialized');
    }
  });

  // Expose utilities for other scripts
  window.EtchedReflection = {
    debounce,
    HeaderScroll,
    LazyLoad,
    MotionPreference,
    SearchOverlay
  };

})();

